import React, { useState } from "react";
import RIcon from "../../reservas/components/ReservasIcons";
import { BEAUTY_FALLBACK_IMAGE, resolveInstagramProfile, resolveUrl } from "../../reservas/components/ReservaShared";
import { buildMapQuery } from "../../canchas/components/mapConfig";
import {
  formatDuration,
  formatMoney,
  groupServicesByCategory,
  openingHoursRows,
  opcionesPill,
  salonAddressLine,
  salonMapsUrl,
  salonPolicyLines,
  serviceMeta,
} from "../beautyBooking";
import { tieneOpciones } from "../../../helpers/beautyLineas";

export const sectionId = (key) => `beauty-${key}`;

export const DepositBadge = ({ deposit }) =>
  Number(deposit) > 0 ? (
    <span className="beauty-badge beauty-badge--deposit">Seña {formatMoney(deposit)}</span>
  ) : (
    <span className="beauty-badge beauty-badge--free">Sin seña</span>
  );

/**
 * One salon page section. Desktop mirrors the Canchas info view
 * (`bd-section-stacked` + `h3.section-title`); mobile mirrors its landing
 * (`rmob-section` + `h3.rmob-section-title`).
 */
export const Section = ({ id, title, isDesktop = false, children }) => (
  <div id={sectionId(id)} className={isDesktop ? "bd-section-stacked" : "rmob-section"}>
    <h3 className={isDesktop ? "section-title" : "rmob-section-title"}>{title}</h3>
    {children}
  </div>
);

/**
 * Services grouped by category. When `onAdd` is provided each row gets the
 * "+ Agregar" / "Agregado" control (desktop cart); otherwise rows are
 * read-only (mobile landing, where the wizard picks services later).
 */
export const MOBILE_SERVICES_LIMIT = 4;

export const ServicesSection = ({ services, cartIds = [], onAdd, onEdit, onRemove, isDesktop, title = "Servicios" }) => {
  const [expanded, setExpanded] = useState(false);
  // The read-only mobile landing shows the first services and a link for the rest.
  const truncated = !isDesktop && !expanded && services.length > MOBILE_SERVICES_LIMIT;
  const visible = truncated ? services.slice(0, MOBILE_SERVICES_LIMIT) : services;
  return (
  <Section id="servicios" title={title} isDesktop={isDesktop}>
    {isDesktop && onAdd && (
      <p className="beauty-text beauty-services-intro">
        Sumá uno o más servicios a tu turno. Los horarios se calculan con la duración total.
      </p>
    )}
    {groupServicesByCategory(visible).map((group) => (
      <div className="beauty-service-group" key={group.category}>
        <h4 className="beauty-group-title">{group.category}</h4>
        <ul className="beauty-service-list">
          {group.services.map((service) => {
            const added = cartIds.includes(String(service._id));
            const conOpciones = tieneOpciones(service);
            return (
              <li
                key={service._id}
                className={`beauty-service${added ? " is-added" : ""}`}
                data-testid="beauty-service"
              >
                <div className="beauty-service-info">
                  <div className="beauty-service-name">{service.name}</div>
                  {service.description && <div className="beauty-service-desc">{service.description}</div>}
                  <div className="beauty-service-meta">{serviceMeta(service)}</div>
                  {conOpciones && <span className="beauty-soft-pill">{opcionesPill(service)}</span>}
                </div>
                <div className="beauty-service-side">
                  <DepositBadge deposit={service.deposit} />
                  {onAdd && !added && (
                    <button
                      type="button"
                      className="r-btn-soft beauty-add-btn"
                      aria-label={conOpciones ? `Elegir opciones de ${service.name}` : `Agregar ${service.name}`}
                      onClick={() => onAdd(service)}
                    >
                      {conOpciones ? "Elegir" : "+ Agregar"}
                    </button>
                  )}
                  {onAdd && added && (
                    <span className="beauty-added">
                      <RIcon name="check" width={14} height={14} /> Agregado
                      {conOpciones && onEdit && (
                        <button
                          type="button"
                          className="r-btn-soft beauty-add-btn"
                          aria-label={`Editar opciones de ${service.name}`}
                          onClick={() => onEdit(service)}
                        >
                          Editar
                        </button>
                      )}
                      {onRemove && (
                        <button
                          type="button"
                          className="beauty-remove-btn"
                          aria-label={`Quitar ${service.name}`}
                          onClick={() => onRemove(service)}
                        >
                          <RIcon name="x" width={12} height={12} />
                        </button>
                      )}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    ))}
    {truncated && (
      <button type="button" className="beauty-link-btn beauty-services-more" onClick={() => setExpanded(true)}>
        Ver los {services.length} servicios
      </button>
    )}
  </Section>
  );
};

export const ProfessionalsSection = ({ professionals, isDesktop, title = "Profesionales" }) => (
  <Section id="profesionales" title={title} isDesktop={isDesktop}>
    <div className="details-grid-premium">
      {professionals.map((pro) => (
        <div className="detail-item-premium" key={pro.id || pro._id}>
          <span className="icon">
            {pro.photo ? (
              <img className="beauty-pro-photo" src={resolveUrl(pro.photo)} alt={pro.name} />
            ) : (
              <span aria-hidden="true">{(pro.name || "?").trim().charAt(0).toUpperCase()}</span>
            )}
          </span>
          <div className="info">
            <span className="label">{pro.specialty || "Profesional"}</span>
            <strong className="val">{pro.name}</strong>
          </div>
        </div>
      ))}
    </div>
  </Section>
);

export const DescriptionSection = ({ text, isDesktop }) => (
  <Section id="descripcion" title="Descripción" isDesktop={isDesktop}>
    <p className={isDesktop ? "beauty-text" : "rmob-desc"}>{text}</p>
  </Section>
);

export const GallerySection = ({ images, name, isDesktop }) => (
  <Section id="galeria" title="Galería" isDesktop={isDesktop}>
    {isDesktop ? (
      <div className="gallery-layout-premium">
        {images.slice(0, 5).map((src, i) => (
          <div key={`${src}-${i}`} className={`gallery-img-wrapper item-${i}`}>
            <img
              src={resolveUrl(src)}
              alt={`${name} galería ${i + 1}`}
              onError={(e) => { e.currentTarget.src = BEAUTY_FALLBACK_IMAGE; }}
            />
          </div>
        ))}
      </div>
    ) : (
      <div className="rmob-gallery-carousel">
        {images.map((src, i) => (
          <div key={`${src}-${i}`} className="rmob-gallery-item">
            <img
              src={resolveUrl(src)}
              alt={`${name} foto ${i + 1}`}
              onError={(e) => { e.currentTarget.src = BEAUTY_FALLBACK_IMAGE; }}
            />
          </div>
        ))}
      </div>
    )}
  </Section>
);

export const LocationSection = ({ salon, isDesktop }) => {
  const line = salonAddressLine(salon);
  const mapsUrl = salonMapsUrl(salon);
  const mapQuery = buildMapQuery(salon);
  return (
    <Section id="ubicacion" title="Ubicación" isDesktop={isDesktop}>
      {line && (
        <p className={isDesktop ? "beauty-text beauty-location" : "rmob-desc beauty-location"}>
          <RIcon name="pin" width={16} height={16} /> {line}
        </p>
      )}
      {mapQuery && (
        <div className={`beauty-map${isDesktop ? "" : " is-mobile"}`}>
          <iframe
            src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
            width="100%"
            height="100%"
            style={{ border: 0, display: "block" }}
            allowFullScreen=""
            loading="lazy"
            title="Google Maps"
          />
        </div>
      )}
      {mapsUrl && (
        <a className="beauty-link" href={mapsUrl} target="_blank" rel="noopener noreferrer">
          Cómo llegar
        </a>
      )}
    </Section>
  );
};

export const HoursSection = ({ openingHours, isDesktop }) => {
  const rows = openingHoursRows(openingHours);
  return (
    <Section id="horarios" title="Horarios de atención" isDesktop={isDesktop}>
      <div className="hours-list-premium">
        {rows.map((row) => (
          <div
            key={row.weekday}
            className={`hours-row-premium${row.isToday ? " today" : ""}${row.closed ? " closed" : ""}`}
          >
            <span className="d">{row.label}</span>
            <span className="h">{row.text}</span>
          </div>
        ))}
      </div>
    </Section>
  );
};

export const ContactSection = ({ salon, isDesktop }) => {
  const instagram = resolveInstagramProfile(salon?.instagram);
  const phone = salon.contactPhone
    ? <a href={`tel:${String(salon.contactPhone).replace(/\s+/g, "")}`}>{salon.contactPhone}</a>
    : null;
  const insta = instagram
    ? <a href={instagram.href} target="_blank" rel="noopener noreferrer">{instagram.handle}</a>
    : null;
  return (
    <Section id="contacto" title="Contacto" isDesktop={isDesktop}>
      {isDesktop ? (
        <div className="details-grid-premium">
          {phone && (
            <div className="detail-item-premium">
              <span className="icon"><RIcon name="phone" width={18} height={18} /></span>
              <div className="info">
                <span className="label">Celular</span>
                <strong className="val">{phone}</strong>
              </div>
            </div>
          )}
          {insta && (
            <div className="detail-item-premium">
              <span className="icon"><RIcon name="instagram" width={18} height={18} /></span>
              <div className="info">
                <span className="label">Instagram</span>
                <strong className="val">{insta}</strong>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="rmob-contact-list">
          {phone && (
            <div className="rmob-contact-row">
              <RIcon name="phone" width={16} height={16} />
              <span>Celular {phone}</span>
            </div>
          )}
          {insta && (
            <div className="rmob-contact-row">
              <RIcon name="instagram" width={16} height={16} />
              <span>Instagram {insta}</span>
            </div>
          )}
        </div>
      )}
    </Section>
  );
};

export const PoliciesSection = ({ salon, isDesktop }) => {
  const lines = salonPolicyLines(salon);
  if (lines.length === 0) return null;
  return (
    <Section id="politicas" title="Políticas del local" isDesktop={isDesktop}>
      {lines.map((line) => (
        <div className="policy" key={line}>
          <RIcon name="shield" width={16} height={16} />
          {line}
        </div>
      ))}
    </Section>
  );
};

/** Which optional sections have content, plus the tab entries for them. */
export const getSalonSections = ({ salon, services, professionals, sucursales = [] }) => {
  const description = (salon?.description || salon?.shortDescription || "").trim();
  const hasHours = openingHoursRows(salon?.openingHours).length > 0;
  const hasContact = Boolean(salon?.contactPhone) || Boolean(resolveInstagramProfile(salon?.instagram));
  const gallery = Array.isArray(salon?.galleryimage) ? salon.galleryimage.filter(Boolean) : [];
  const hasLocation = Boolean(salonAddressLine(salon)) || Boolean(salonMapsUrl(salon));
  const hasPolicies = salonPolicyLines(salon).length > 0;
  const nav = [
    // A casa central opens with where to book; everything below belongs to the chosen sede.
    sucursales.length > 0 && { key: "sucursales", label: "Sucursales" },
    services.length > 0 && { key: "servicios", label: "Servicios" },
    professionals.length > 0 && { key: "profesionales", label: "Profesionales" },
    description && { key: "descripcion", label: "Descripción" },
    gallery.length > 0 && { key: "galeria", label: "Galería" },
    hasLocation && { key: "ubicacion", label: "Ubicación" },
    hasHours && { key: "horarios", label: "Horarios" },
    hasContact && { key: "contacto", label: "Contacto" },
    hasPolicies && { key: "politicas", label: "Políticas" },
    { key: "resenas", label: "Reseñas" },
  ].filter(Boolean);
  return { description, hasHours, hasContact, gallery, hasLocation, nav };
};
